// Утилита «схлопывания дочерних помещений» (паркинги/кладовки) в группы услуг родителя.
// Используется ViewModel табов раздела «Сальдо» (ОСИ / Помещения / Начисления).
// Логика collapseOsvChildren — точная копия бэкенда MergeChildAbonentsIntoParents:
// дочерние услуги вливаются в одноимённые услуги родителя, числовые поля суммируются.

import { AreaTypeCodes } from '@shared/types/dictionaries';
import { Abonent } from '@shared/types/osi/abonents';
import type { ReportsOSVAbonent, ReportsOSVSaldo } from '@/shared/api/orval/models';

// Тип числового ключа услуги ОСВ — только поля со значением number (исключаем serviceName/serviceNameKz).
type SaldoNumericKey = {
  [K in keyof ReportsOSVSaldo]-?: NonNullable<ReportsOSVSaldo[K]> extends number ? K : never;
}[keyof ReportsOSVSaldo];

// Числовые поля услуги ОСВ (ReportsOSVSaldo) — суммируются при слиянии ребёнка в родителя.
// Точный список взят из модели reportsOSVSaldo.ts.
export const SALDO_NUMERIC_FIELDS: SaldoNumericKey[] = [
  'begin',
  'debet',
  'debetWithoutFixes',
  'sumOfFixes',
  'sumOfAccurals',
  'sumOfFines',
  'kredit',
  'end'
];

// true, если тип помещения — дочерний (паркинг или кладовка).
export const isChildAreaType = (code?: string): boolean =>
  code === AreaTypeCodes.PARKING || code === AreaTypeCodes.STORAGE;

// Строит карту childId→parentId только для абонентов,
// у которых задан parentAbonentId и тип помещения — дочерний.
export const buildChildToParent = (abonents: Abonent[]): Map<number, number> => {
  const map = new Map<number, number>();
  abonents.forEach((abonent) => {
    // нужны id ребёнка, ссылка на родителя и дочерний тип помещения
    if (
      abonent.id != null &&
      abonent.parentAbonentId != null &&
      isChildAreaType(abonent.areaTypeCode)
    ) {
      map.set(abonent.id, abonent.parentAbonentId);
    }
  });
  return map;
};

// Прибавляет числовые поля услуги-источника к услуге-приёмнику (иммутабельно — возвращает новый объект).
const addSaldoServices = (target: ReportsOSVSaldo, source: ReportsOSVSaldo): ReportsOSVSaldo => {
  const result: ReportsOSVSaldo = { ...target };
  SALDO_NUMERIC_FIELDS.forEach((field) => {
    result[field] = (target[field] ?? 0) + (source[field] ?? 0);
  });
  return result;
};

// Схлопывает услуги дочерних абонентов в услуги их родителей.
// Иммутабельно: вход не мутируется, создаются новые объекты абонентов и услуг.
export function collapseOsvChildren(
  osv: ReportsOSVAbonent[],
  childToParent: Map<number, number>
): ReportsOSVAbonent[] {
  // карта abonentId→копия абонента с копиями услуг; сохраняем исходный порядок abonentId
  const byId = new Map<number, ReportsOSVAbonent>();
  const order: number[] = [];

  osv.forEach((abonent) => {
    if (abonent.abonentId == null) {
      return;
    }
    // копия абонента с копиями его услуг (services readonly — не мутируем исходные)
    const copy: ReportsOSVAbonent = {
      ...abonent,
      services: (abonent.services ?? []).map((s) => ({ ...s }))
    };
    byId.set(abonent.abonentId, copy);
    order.push(abonent.abonentId);
  });

  // вливаем каждого ребёнка в его родителя
  childToParent.forEach((parentId, childId) => {
    // ребёнка нет в выборке — пропускаем
    const child = byId.get(childId);
    if (!child) {
      return;
    }
    // родителя нет в выборке — ребёнок остаётся как есть
    const parent = byId.get(parentId);
    if (!parent) {
      return;
    }

    // services у копии — обычный массив объектов (создан выше через map)
    const parentServices = (parent.services as ReportsOSVSaldo[]) ?? [];
    const childServices = (child.services as ReportsOSVSaldo[]) ?? [];

    childServices.forEach((childService) => {
      // ищем у родителя услугу с тем же serviceName
      const index = parentServices.findIndex((s) => s.serviceName === childService.serviceName);
      if (index >= 0) {
        // есть одноимённая — суммируем числовые поля
        parentServices[index] = addSaldoServices(parentServices[index], childService);
      } else {
        // нет — добавляем копию услуги ребёнка родителю
        parentServices.push({ ...childService });
      }
    });

    // parentServices — это та же ссылка на массив услуг копии родителя (создан выше через map),
    // мутации findIndex/push уже применились к parent.services, отдельное присваивание не нужно
    // (services помечен readonly в модели — переприсваивать его нельзя).
    // ребёнок влит в родителя — убираем его из карты
    byId.delete(childId);
  });

  // возвращаем оставшихся абонентов в исходном порядке
  return order.map((id) => byId.get(id)).filter((a): a is ReportsOSVAbonent => a != null);
}

// --- mergeFlatOsvByPeriod ---
// Вход — ответы api.getOsvOnAllPeriods (вход getOsvRowsByFlat).
// Реальная форма (см. helpers/osv.ts → getOsvRowsByFlat): массив периодов
// { period: string; services: Array<{ serviceName; serviceNameKz; begin; debet; ... } > }.
// Типобезопасных orval-моделей для этой формы нет, поэтому используем any[].

// Период из ответа getOsvOnAllPeriods: ключ периода — period, услуги — в services.
type OsvPeriod = {
  period?: string | null;
  services?: any[] | null;
  [key: string]: any;
};

// Сливает услуги дочерних абонентов в услуги родителя ПО КАЖДОМУ ПЕРИОДУ.
// Внешний ключ — period, внутренний — serviceName. Суммируются те же числовые поля.
// Иммутабельно: возвращается новый массив периодов той же формы.
export function mergeFlatOsvByPeriod(parentData: OsvPeriod[], childrenData: OsvPeriod[][]): OsvPeriod[] {
  // копия периодов родителя с копиями услуг; индекс по ключу периода для слияния
  const periods: OsvPeriod[] = (parentData ?? []).map((p) => ({
    ...p,
    services: (p.services ?? []).map((s) => ({ ...s }))
  }));
  const periodByKey = new Map<string, OsvPeriod>();
  periods.forEach((p) => {
    periodByKey.set(String(p.period), p);
  });

  // по каждому ребёнку и каждому его периоду вливаем услуги в соответствующий период родителя
  (childrenData ?? []).forEach((childPeriods) => {
    (childPeriods ?? []).forEach((childPeriod) => {
      const key = String(childPeriod.period);
      const target = periodByKey.get(key);
      if (!target) {
        // у родителя нет такого периода — пропускаем (схлопываем только в существующие периоды родителя)
        return;
      }
      const targetServices = target.services as any[];
      (childPeriod.services ?? []).forEach((childService) => {
        const index = targetServices.findIndex((s) => s.serviceName === childService.serviceName);
        if (index >= 0) {
          // суммируем числовые поля одноимённой услуги
          const merged = { ...targetServices[index] };
          SALDO_NUMERIC_FIELDS.forEach((field) => {
            merged[field] = (targetServices[index][field] ?? 0) + (childService[field] ?? 0);
          });
          targetServices[index] = merged;
        } else {
          // нет одноимённой — добавляем копию услуги ребёнка
          targetServices.push({ ...childService });
        }
      });
    });
  });

  return periods;
}

// --- mergeAccrualGroups ---
// Вход — массивы групп начислений (форма из getRows / ответа accurals-by-abonent-and-services):
// группа { groupName; services: Array<{ serviceName; accural; fix; total }> }.

// Числовые поля начислений (ReportsAccuralsByAbonentAndServicesService) — суммируются при слиянии.
export const ACCRUAL_NUMERIC_FIELDS = ['accural', 'fix', 'total'] as const;

type AccrualService = {
  serviceName?: string | null;
  accural?: number;
  fix?: number;
  total?: number;
  [key: string]: any;
};

type AccrualGroup = {
  groupName?: string | null;
  services?: AccrualService[] | null;
  [key: string]: any;
};

// Сливает группы начислений детей в группы родителя.
// Внешний ключ — groupName, внутренний — serviceName. Суммируются accural/fix/total.
// Иммутабельно: возвращается новый массив групп той же формы.
export function mergeAccrualGroups(parentGroups: AccrualGroup[], childrenGroups: AccrualGroup[][]): AccrualGroup[] {
  // копия групп родителя с копиями услуг; индекс по groupName
  const groups: AccrualGroup[] = (parentGroups ?? []).map((g) => ({
    ...g,
    services: (g.services ?? []).map((s) => ({ ...s }))
  }));
  const groupByName = new Map<string, AccrualGroup>();
  groups.forEach((g) => {
    groupByName.set(String(g.groupName), g);
  });

  // по каждому ребёнку и каждой его группе вливаем услуги
  (childrenGroups ?? []).forEach((childGroups) => {
    (childGroups ?? []).forEach((childGroup) => {
      const key = String(childGroup.groupName);
      let target = groupByName.get(key);
      if (!target) {
        // у родителя нет такой группы — добавляем копию группы ребёнка
        target = {
          ...childGroup,
          services: (childGroup.services ?? []).map((s) => ({ ...s }))
        };
        groups.push(target);
        groupByName.set(key, target);
        return;
      }
      const targetServices = target.services as AccrualService[];
      (childGroup.services ?? []).forEach((childService) => {
        const index = targetServices.findIndex((s) => s.serviceName === childService.serviceName);
        if (index >= 0) {
          // суммируем числовые поля одноимённой услуги
          const merged: AccrualService = { ...targetServices[index] };
          ACCRUAL_NUMERIC_FIELDS.forEach((field) => {
            merged[field] = (targetServices[index][field] ?? 0) + (childService[field] ?? 0);
          });
          targetServices[index] = merged;
        } else {
          // нет одноимённой — добавляем копию услуги ребёнка
          targetServices.push({ ...childService });
        }
      });
    });
  });

  return groups;
}
